/* eslint-disable react-hooks/exhaustive-deps */
import React, { lazy, Suspense, useMemo, useState } from "react";
import { useAppSelector } from "hooks/redux";

import {
    BlockWrapper,
    CPSettingsBlockWrapper,
    CustomPlusCircled,
    SettingsBlockHeader,
    SettingsBlockTitle,
} from "pages/settings/settingsPageStyles";
import { TrashIcon } from "assets/images";
import Loader from "components/Loader/Loader";
import {
    updateConfigValue,
    TickerConfig,
    removeDisplayedCommonParam,
    updateColumnConfig,

    setConfigValidationError,
    ConfigFileDetails,
} from "../../../store/reducers/SettingsSlice";
import { useDispatch } from "react-redux";
import error from "../../../utils/Notifications/Error";
import {
    ModalContentWrapper,
    SelectDiv,
    TickersSelectWrapper,
    TickersTitle,
    TopElementsDiv,
} from "./CommonSettingsBlockStyles";
import { CommonSettingsItem } from "./CommonSettingsItem";
import TPH from "utils/trade-params-handler";

const AddCPParamsModal = lazy(
    () => import("components/AddCommonParamsModal/AddCommonParamsModal"),
);

const CommonSettings = ({
    config,
    isSavingError,
    activeTickerName,
    groupId,
    handleDeleteConfig,
    configMetadata,
}: {
    config: TickerConfig;
    isSavingError: boolean;
    activeTickerName: string;
    groupId: string;
    handleDeleteConfig: (configId: number) => void;
    configMetadata?: ConfigFileDetails;
}) => {
    const { id } = config;
    const dispatch = useDispatch();
    const [showTrash, setShowTrash] = useState(false);
    const savedConfig = useAppSelector((state) =>
        state.settings.saved_configs.find((c) => c.id === id),
    );
    const { selectedRobotId, robotStatuses } = useAppSelector(
        (state) => state.robots,
    );
    const editMode = useAppSelector((state) => state.settings.editMode);
    const currentRobotStatus = selectedRobotId
        ? robotStatuses[selectedRobotId]?.status
        : null;
    const isRobotStopped = currentRobotStatus === "robot_stopped";

    const configValues = savedConfig?.values;
    const keys: string[] = Array.isArray(configValues)
        ? configValues[0] || []
        : [];
    const valuesRow: string[] = Array.isArray(configValues)
        ? configValues[1] || []
        : [];

    const [addModalOpen, setAddModalOpen] = useState(false);

    const displayedParams = useMemo(() => {
        if (keys.length === 0) return [];

        const seen = new Set<string>();
        const result: { key: string; value: string }[] = [];

        keys.forEach((key, idx) => {
            const trimmedKey = key.trim();
            if (trimmedKey === "") return;

            if (!seen.has(trimmedKey)) {
                seen.add(trimmedKey);
                result.push({
                    key: trimmedKey,
                    value: valuesRow[idx] || "",
                });
            }
        });

        return result;
    }, [keys, valuesRow]);

    const allKeyValueConfigsOfSameType = useAppSelector((state) =>
        state.settings.saved_configs.filter(
            (c) =>
                c.config_code === config.config_code &&
                c.id !== id && // не текущий конфиг
                !state.settings.configFilesDetails[c.config_code]?.is_uniq_ui,
        ),
    );

    const activeTicker = useAppSelector((state) => state.settings.activeTicker);

    const usedInOtherConfigs = useMemo(() => {
        const used = new Set<string>();

        allKeyValueConfigsOfSameType.forEach((cfg) => {
            const cpKeys = cfg.values?.[0] || [];
            cpKeys.forEach((k: string) => {
                if (k && k.trim() !== "") used.add(k.trim());
            });
        });

        return used;
    }, [allKeyValueConfigsOfSameType]);

    const availableParams = useMemo(() => {
        if (
            !savedConfig ||
            !savedConfig.all_values ||
            savedConfig.all_values.length === 0
        ) {
            return [];
        }

        const fullKeys: string[] = savedConfig.all_values[0];

        const currentKeys = new Set(keys.filter((k) => k && k.trim() !== ""));

        return fullKeys.filter((key) => {
            const trimmed = key.trim();
            return (
                !currentKeys.has(trimmed) && !usedInOtherConfigs.has(trimmed)
            );
        });
    }, [savedConfig, keys, usedInOtherConfigs]);

    const handleAddParam = (paramKey: string) => {
        let newValue = "0";
        const defaultValues = savedConfig.all_values[1];
        const fullKeys = savedConfig.all_values[0];

        const index = fullKeys.indexOf(paramKey);

        if (config.config_code === "CP") {
            const isNewTicker = activeTicker.isNew;

            const existing = displayedParams.find(
                (p) => p.key.trim() === paramKey.trim(),
            );

            if (isNewTicker) {
                // ?
                // if (paramKey === "HedgeInstId") {
                //     newValue = "";
                // } else
                     if (paramKey === "ID.depend") {
                    const tickerName = activeTicker?.name || "";
                    newValue = tickerName;
                } else {
                    const fieldMeta = configMetadata?.fields?.find(
                        (f) => f.name === paramKey,
                    );
                    const defaultVal = fieldMeta?.default_value;
                    newValue =
                        defaultVal != null && defaultVal !== ""
                            ? defaultVal
                            : "0";
                }
            } else if (existing) {
                newValue = existing.value || defaultValues[index] || "0";
            } else {
                newValue = defaultValues[index] || "0";
            }
        } else {
            if (!savedConfig?.all_values || savedConfig.all_values.length < 2) {
                newValue = "";
            } else {
                if (index === -1) {
                    return;
                }

                newValue = defaultValues[index] || "";
                if (!newValue.trim()) {
                    newValue = "0";
                }
            }
        }

        dispatch(
            updateConfigValue({
                configId: id,
                paramKey,
                newValue,
            }),
        );

        // setAddModalOpen(false);
    };

    const handleRemoveParam = (paramKey: string) => {
        dispatch(removeDisplayedCommonParam({ configId: id, paramKey }));
        dispatch(
            updateColumnConfig({
                nameTicker: activeTickerName,
                configId: id,
                config_code: config.config_code,
                groupName: groupId,
                isEdited: true,
                columns: displayedParams
                    .filter((item) => item.key !== paramKey)
                    .map((item) => item.key),
            }),
        );
    };
    const handleValueChange = (key: string, value: string) => {
        const rawKey = key.split(" ")[0];
        const type = getFieldMeta(rawKey)?.field_type;
        if (!type) {
            dispatch(
                updateConfigValue({
                    configId: id,
                    paramKey: key,
                    newValue: value,
                }),
            );
            return;
        }

        const isValid = TPH.validateValue(value, type);

        if (!isValid) {
            error(
                `Неправильное значение для параметра "${rawKey}". Ожидается тип: ${type}`,
            );

            dispatch(
                setConfigValidationError({
                    configId: id,
                    fieldKey: rawKey,
                    hasError: true,
                }),
            );
            setParamsError([
                ...paramsError.filter((param) => param.key !== key),
                {
                    key: key,
                    value: true,
                },
            ]);
            return;
        } else {
            dispatch(
                setConfigValidationError({
                    configId: id,
                    fieldKey: rawKey,
                    hasError: false,
                }),
            );

            setParamsError([
                ...paramsError.filter((param) => param.key !== key),
                {
                    key: key,
                    value: false,
                },
            ]);
        }

        dispatch(
            updateConfigValue({
                configId: id,
                paramKey: key,
                newValue: value,
            }),
        );

    };

    const [paramsError, setParamsError] = useState(
        displayedParams.map((param) => {
            return { key: param.key, value: false };
        }),
    );

    const getFieldMeta = (fieldName: string) => {
        return configMetadata?.fields?.find((f) => f.name === fieldName);
    };

    return (
        <CPSettingsBlockWrapper>
            <SettingsBlockHeader
                onMouseEnter={() => setShowTrash(true)}
                onMouseLeave={() => setShowTrash(false)}
                style={{ position: "relative" }}
            >
                <SettingsBlockTitle>
                    {savedConfig.config_code}
                    {configMetadata?.description && (
                        <span
                            style={{
                                color: "#888",
                                fontSize: "0.9em",
                                marginLeft: 8,
                            }}
                        >
                            — {configMetadata.description}
                        </span>
                    )}
                </SettingsBlockTitle>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {showTrash && editMode && (
                        <TrashIcon
                            onClick={() => handleDeleteConfig(config.id)}
                            style={{
                                width: 20,
                                height: 20,
                                cursor: "pointer",
                                stroke: "#ff4d4f",
                                opacity: 0.85,
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = "1")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = "0.85")
                            }
                        />
                    )}
                    {availableParams.length > 0 && editMode && (
                        <CustomPlusCircled
                            onClick={() => setAddModalOpen(true)}
                        />
                    )}
                </div>
            </SettingsBlockHeader>

            <BlockWrapper>
                <ModalContentWrapper>
                    <TickersSelectWrapper>
                        <TopElementsDiv>
                            <TickersTitle>Название</TickersTitle>
                            <TickersTitle>Значение</TickersTitle>
                        </TopElementsDiv>
                        <SelectDiv>
                            {displayedParams.map(({ key, value }) => {
                                const rawKey = key.split(" ")[0];

                                const fieldMeta = getFieldMeta(rawKey);
                                const paramType = fieldMeta?.field_type;
                                const isFieldEditable = fieldMeta?.is_editable;
                                let isDisabled = false;

                                if (configMetadata?.readonly) {
                                    isDisabled = true;
                                } else if (isFieldEditable === false) {
                                    isDisabled = true;
                                } else if (
                                    configMetadata?.is_robot_stopped === true
                                ) {
                                    if (!isRobotStopped) {
                                        isDisabled = true;
                                    }
                                }
                                let placeholder = "";

                                if (paramType === "Integer") {
                                    placeholder = "целое число";
                                } else if (paramType === "Float") {
                                    placeholder = "число";
                                } else if (paramType === "Boolean") {
                                    placeholder = "0 или 1";
                                }
                                return (
                                    <CommonSettingsItem
                                        isSavingError={isSavingError}
                                        isInputError={
                                            paramsError?.find(
                                                (param) => param.key === key,
                                            )?.value
                                        }
                                        key={`${activeTickerName}-${id}-${key}`}
                                        name={key}
                                        disabled={isDisabled}
                                        placeholder={placeholder}
                                        currentValue={value}
                                        isInput={true}
                                        onChange={(val: string) =>
                                            handleValueChange(key, val)
                                        }
                                        configCode={config.config_code}
                                        onRemove={() => handleRemoveParam(key)}
                                    />
                                );
                            })}
                        </SelectDiv>
                    </TickersSelectWrapper>
                </ModalContentWrapper>
            </BlockWrapper>

            <Suspense fallback={<Loader size="large" />}>
                <AddCPParamsModal
                    isOpen={addModalOpen}
                    onRequestClose={() => {
                        setAddModalOpen(false);
                        dispatch(
                            updateColumnConfig({
                                nameTicker: activeTickerName,
                                configId: id,
                                config_code: config.config_code,
                                groupName: groupId,
                                columns: displayedParams.map(
                                    (item) => item.key,
                                ),
                            }),
                        );
                    }}
                    onChoose={handleAddParam}
                    data={Object.fromEntries(
                        availableParams.map((key) => [key, ""]),
                    )}
                />
            </Suspense>
        </CPSettingsBlockWrapper>
    );
};

export default CommonSettings;
